/**
 * The MUI theme the dropdowns are drawn with (components/Select.jsx), the same Autocomplete
 * and Select that InfoCodes (s4ca-file-maker) uses for every picker.
 *
 * The colours are not written here. styles.css owns them — light and dark, and the six
 * palettes on the Appearance page — and MUI needs real colours rather than var() references,
 * because it works out hover shades and contrast text from them. So the theme is built from
 * the tokens as the document has them now, and rebuilt whenever ThemeProvider changes
 * data-theme or data-scheme. The same six accents are InfoCodes' six plain presets, so a
 * picker here and a picker there are painted alike.
 *
 * The type is the archive's 13px rather than MUI's 16px. With it, MUI's own small field
 * padding comes out at the 36px of the archive's .input and date fields, so a picker sits
 * level with them in a row of filters without any padding of its own.
 */
import { createTheme } from '@mui/material/styles'
import { autocompleteClasses } from '@mui/material/Autocomplete'
import { outlinedInputClasses } from '@mui/material/OutlinedInput'
import { chipClasses } from '@mui/material/Chip'

/* What styles.css's :root holds, for a document that cannot be read (a test, or a token
   missing because the stylesheet has not loaded yet). Emerald, light. */
const FALLBACK = {
  '--bg': '#f6f7f9',
  '--bg-elev': '#ffffff',
  '--bg-sunken': '#eef0f4',
  '--bg-hover': '#f0f2f6',
  '--fg': '#14161a',
  '--fg-muted': '#5b6472',
  '--fg-subtle': '#656e7c',
  '--border': '#e2e5ea',
  '--border-strong': '#cdd2da',
  '--accent': '#0b7a55',
  '--accent-fg': '#ffffff',
  '--danger': '#c02626',
  '--font-sans': "'Work Sans Variable', ui-sans-serif, system-ui, sans-serif",
}

/** The tokens the theme is built from, read off <html> as they stand. */
export function readTokens() {
  const css = typeof document === 'undefined' ? null : getComputedStyle(document.documentElement)
  const out = {}
  for (const [name, fallback] of Object.entries(FALLBACK)) {
    out[name] = css?.getPropertyValue(name).trim() || fallback
  }
  return out
}

export function buildMuiTheme(mode, t) {
  return createTheme({
    palette: {
      mode,
      primary: { main: t['--accent'], contrastText: t['--accent-fg'] },
      error: { main: t['--danger'] },
      background: { default: t['--bg'], paper: t['--bg-elev'] },
      text: { primary: t['--fg'], secondary: t['--fg-muted'], disabled: t['--fg-subtle'] },
      divider: t['--border'],
      action: { hover: t['--bg-hover'] },
    },
    shape: { borderRadius: 6 },
    typography: {
      fontFamily: t['--font-sans'],
      // Fields, menu rows and list rows all set their text in body1.
      body1: { fontSize: '0.8125rem' },
    },
    /* The archive's own ladder (styles.css): over a dialog (--z-modal, 100), under the
       toasts (--z-toast, 800) and the cover shown while signing out (--z-leaving, 900).
       Autocomplete's list and the Select's menu both take their layer from zIndex.modal,
       which is why it is the one moved; it is --z-dropdown's 400. */
    zIndex: { modal: 400 },
    components: {
      MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiAutocomplete: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          tag: { [`&.${chipClasses.sizeSmall}`]: { height: 22, fontSize: 'var(--fs-xs)' } },
          paper: { border: `1px solid ${t['--border']}`, boxShadow: 'var(--shadow-lg)', marginTop: 4 },
          listbox: { padding: 4, [`& .${autocompleteClasses.option}`]: { borderRadius: 6, minHeight: 32 } },
          groupLabel: {
            backgroundColor: t['--bg-elev'],
            color: t['--fg-subtle'],
            fontSize: 'var(--label-size)',
            fontWeight: 'var(--label-weight)',
            letterSpacing: 'var(--label-tracking)',
            textTransform: 'uppercase',
            lineHeight: '28px',
          },
          noOptions: { fontSize: 'var(--fs-sm)', color: t['--fg-subtle'] },
          loading: { fontSize: 'var(--fs-sm)', color: t['--fg-subtle'] },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: t['--bg-elev'],
            [`& .${outlinedInputClasses.notchedOutline}`]: { borderColor: t['--border-strong'] },
            [`&.${outlinedInputClasses.disabled}`]: { backgroundColor: t['--bg-sunken'] },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { border: `1px solid ${t['--border']}`, boxShadow: 'var(--shadow-lg)' },
          list: { padding: 4 },
        },
      },
      MuiMenuItem: {
        defaultProps: { dense: true },
        styleOverrides: { root: { borderRadius: 6, minHeight: 32 } },
      },
      MuiCheckbox: { defaultProps: { size: 'small' } },
    },
  })
}
